import { useEffect, useState } from 'react'
import { buyerApi, type BuyerBill, type BuyerProfile } from '../lib/buyer'
import { money, shortDate, titleCase } from '../lib/format'
import { Banner, Empty, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { BuyerHeader } from './BuyerShell'

export function MyBills() {
  const [bills, setBills] = useState<BuyerBill[]>([])
  const [me, setMe] = useState<BuyerProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      buyerApi.get<BuyerBill[]>('/shop/bills'),
      buyerApi.get<BuyerProfile>('/shop/me'),
    ])
      .then(([b, profile]) => { setBills(b); setMe(profile) })
      .catch(() => setBills([]))
      .finally(() => setLoading(false))
  }, [])

  const open = bills.filter((b) => Number(b.balance) > 0)
  const overdue = open.filter((b) => b.daysOverdue > 0)

  return (
    <>
      <BuyerHeader />
      <div className="field-greet">
        <h1>Bills</h1>
        <p>What you have been invoiced, and what is still to pay.</p>
      </div>

      <div className="field-body">
        {loading && <Panel><Loading rows={4} /></Panel>}

        {me && (
          <div className="mini-row">
            <StatMini label="You owe" value={money(me.payment.owing, { compact: true })} />
            <StatMini label="Past due" value={money(me.payment.overdue, { compact: true })}
              note={overdue.length > 0 ? `${overdue.length} invoice(s)` : 'nothing late'} />
            <StatMini
              label="How you pay"
              value={me.payment.terms === 'cash_only' ? 'On delivery' : `${me.payment.termsDays} days`}
            />
          </div>
        )}

        {overdue.length > 0 && (
          <Banner tone="warn" title={`${money(me?.payment.overdue ?? 0)} is past its due date`}>
            Clearing it keeps your deliveries going out without anybody having to call you.
          </Banner>
        )}

        {!loading && bills.length === 0 && (
          <Empty title="No bills yet">
            An invoice is raised after a delivery, for what you actually took.
          </Empty>
        )}

        {bills.length > 0 && (
          <Panel title="Invoices" meta={`${bills.length}`}>
            <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {bills.map((b) => {
                const paid = Number(b.balance) <= 0
                const late = b.daysOverdue > 0 && !paid
                return (
                  <div key={b.id} className="stop-row" style={{ cursor: 'default' }}>
                    <span className={`stop-state ${paid ? 'done' : late ? 'pending' : 'pending'}`}>
                      {paid ? '✓' : '·'}
                    </span>
                    <span className="stop-who">
                      <b>{money(b.total, { currency: false })}</b>
                      <i>
                        <span className="mono">{b.number}</span> · due {shortDate(b.dueDate)}
                        {b.deliveryCode && ` · ${b.deliveryCode}`}
                      </i>
                    </span>
                    <span className="stop-amt">
                      <b className={paid ? 'ok-t' : late ? 'crit-t' : ''}>
                        {paid ? 'Paid' : money(b.balance, { currency: false })}
                      </b>
                      <span>
                        {late
                          ? <Pill tone="crit" dot={false}>{b.daysOverdue} d late</Pill>
                          : titleCase(b.status)}
                      </span>
                    </span>
                  </div>
                )
              })}
            </div>
          </Panel>
        )}

        {bills.some((b) => b.taxInvoiceNo) && (
          <Banner tone="info" title="Your invoices are tax invoices">
            Each one carries a KRA number, so you can claim it in your own books.
          </Banner>
        )}
      </div>
    </>
  )
}
